import React, { useMemo, useState } from "react";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import {
  UNIDADES,
  TIPOS_ESCALA,
  ESCALA_DEFAULT,
  MODOS_PIEZAS,
  nuevoIdNoDisponible,
  incluyeDe,
  nombreConVariante,
  precioDe,
  proveedorDe,
  puedeIrIncluido,
  unidadDe,
} from "../../utils/catalogo";
import { useVenue } from "../VenueProvider";
import ProveedorCreateDialog from "../ProveedorCreateDialog";
import ConceptoPicker from "../cotizacion/ConceptoPicker";
import { PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

export const inputClass =
  "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

const UNIDAD_SINGULAR = { evento: "evento", pieza: "pieza", hora: "hora", dia: "día", persona: "persona" };

// EL formulario de servicio (FRA-348). Lo usan el alta, la edición y el
// asistente de clasificación, para que las tres pantallas pidan exactamente
// lo mismo. El estado vive en el caller (`form`/`setForm`, forma de
// utils/servicios.js#formVacio); aquí solo se pinta.
//
// Nada es obligatorio más que el nombre y el precio. Las dos preguntas
// —¿lo da un proveedor? ¿tiene piezas físicas?— son independientes, las dos
// opcionales, y ninguna excluye a la otra. Sin proveedor, el servicio es del
// venue: no hay "ejecutor" que declarar.
//
// `modo`: "alta" muestra la inversión; "edicion" la omite (se registra desde
// la ficha); "clasificar" es como alta pero sin nombre/descripción arriba
// (ya existen).
export default function ServicioForm({
  form,
  setForm,
  proveedores = [],
  modo = "alta",
  autoFocus = false,
  itemId = null,
}) {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const [showNuevoProveedor, setShowNuevoProveedor] = useState(false);

  const proveedoresActivos = useMemo(
    () =>
      (proveedores || [])
        .filter((p) => (p.status || "activo") === "activo" && !p.archivedAt)
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")),
    [proveedores]
  );

  const unidadLabel =
    form.unidad === "otra"
      ? String(form.unidadNombre || "").trim() || "unidad"
      : UNIDAD_SINGULAR[form.unidad] || "unidad";

  // Un descuento no es un servicio: solo nombre, descripción y monto.
  const esDescuento = form.tipo === "descuento";
  // La renta de un espacio: se cobra, trae incluidos, y nada más. No lleva
  // proveedor (es del venue), ni piezas (lo que se renta es el espacio), ni
  // inversión (no se compra).
  const esRentaForm = form.tipo === "renta";
  const esProveedor = !!form.deProveedor && !esRentaForm;
  const esAlta = modo !== "edicion";
  const queEs = esRentaForm ? "renta" : esProveedor ? "proveedor" : "venue";
  const esPool = !!form.itemizado && (form.modoPiezas || "identificadas") === "cantidad";

  const elegirQueEs = (val) => {
    if (val === "renta") {
      set({
        tipo: "renta",
        deProveedor: false,
        proveedorId: "",
        costoProveedor: "",
        itemizado: false,
        incluidoEnRenta: false,
        adquisicionMonto: "",
      });
      return;
    }
    if (val === "proveedor") {
      // Excluyentes: si lo trae un proveedor, las piezas no son tuyas —su
      // disponibilidad es problema suyo— y el evento ya le genera su costo.
      // Y la inversión es de lo que compras, no de lo que te traen.
      set({ tipo: "normal", deProveedor: true, itemizado: false, adquisicionMonto: "" });
      return;
    }
    set({ tipo: "normal", deProveedor: false, proveedorId: "", costoProveedor: "" });
  };

  const setVariante = (idx, patch) =>
    set({ variantes: form.variantes.map((v, i) => (i === idx ? { ...v, ...patch } : v)) });

  return (
    <div className="space-y-5">
      {modo !== "clasificar" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-zinc-500 mb-1">Nombre</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => set({ nombre: e.target.value })}
              placeholder={
                esRentaForm
                  ? "Renta de Salón — Sábado, Renta de Jardín — Viernes (montaje)…"
                  : "Calentador de exterior, Valet parking, Ambulancia…"
              }
              className={inputClass}
              autoFocus={autoFocus}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Descripción (opcional)</label>
            <input
              type="text"
              value={form.descripcion}
              onChange={(e) => set({ descripcion: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {/* ── ¿Qué es? ──
          Una sola fila contesta las dos preguntas que el modelo tiene: si es
          la renta de un espacio (que trae incluidos y nada más) y, si no, de
          quién viene. Antes eran dos tarjetas —venue o proveedor— y la renta
          no existía como cosa aparte: se daba de alta como servicio del venue
          y lo que traía dentro no tenía dónde vivir. */}
      {!esDescuento && (
      <div className="space-y-2">
        <div className="text-xs font-medium text-zinc-500">¿Qué es?</div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            {
              value: "renta",
              label: "La renta de un espacio",
              hint: "Se cobra y trae incluidos. Un evento puede llevar varias.",
            },
            {
              value: "venue",
              label: "Un servicio del venue",
              hint: "Es tuyo. Puedes administrarle piezas y reservarlas por evento.",
            },
            {
              value: "proveedor",
              label: "Un servicio de proveedor",
              hint: "Un tercero lo trae y te cobra: ambulancia, DJ, banquete.",
            },
          ].map((o) => {
            const activo = queEs === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => elegirQueEs(o.value)}
                className={`text-left rounded-lg border p-3 transition ${
                  activo ? "border-transparent" : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300"
                }`}
                style={
                  activo
                    ? { backgroundColor: ACCENT + "0d", boxShadow: `0 0 0 2px ${ACCENT}` }
                    : undefined
                }
              >
                <div className="text-sm font-semibold text-zinc-900 dark:text-white">{o.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{o.hint}</div>
              </button>
            );
          })}
        </div>

        {esRentaForm && (
          <IncluyeEditor
            incluye={form.incluye || []}
            onChange={(incluye) => set({ incluye })}
            itemId={itemId}
            proveedores={proveedores}
          />
        )}

        {esProveedor && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">¿Cuál?</label>
              <select
                value={form.proveedorId}
                onChange={(e) => set({ proveedorId: e.target.value })}
                className={inputClass}
              >
                <option value="">Elegir proveedor…</option>
                {proveedoresActivos.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNuevoProveedor(true)}
                className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Crear proveedor nuevo
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                ¿Cuánto te cobra? (opcional)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={fmtMXNInput(form.costoProveedor)}
                onChange={(e) => set({ costoProveedor: normalizeMoneyInput(e.target.value) })}
                placeholder="$0"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-zinc-500">
                Lo que te cuesta contratarlo por {unidadLabel}. Al asignarlo a un
                evento, su costo se registra solo con esta tarifa.
              </p>
            </div>
          </div>
          </div>
        )}

        {/* Piezas: solo tiene sentido en lo que es del venue. Lo que se renta
            es el espacio; las piezas van en los servicios que la renta trae. */}
        {!esProveedor && !esRentaForm && (
        <Pregunta
          activa={!!form.itemizado}
          titulo="¿Tiene piezas físicas que administrar?"
          hint="Unidades identificables, con estado y mantenimiento: calentadores, luces, mobiliario."
          onToggle={(on) => set({ itemizado: on })}
        >
          <div className="space-y-4">
            {/* Cómo se administran. Antes solo existía una forma —cada pieza un
                renglón— y para 240 sillas eso es impracticable: nadie las da de
                alta una por una, así que el servicio quedaba sin inventario y
                sin disponibilidad. */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-zinc-500">¿Cómo las administras?</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MODOS_PIEZAS.map((m) => {
                  const activo = (form.modoPiezas || "identificadas") === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => set({ modoPiezas: m.value })}
                      className={`text-left rounded-lg border p-2.5 transition ${
                        activo ? "border-transparent" : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300"
                      }`}
                      style={
                        activo
                          ? { backgroundColor: ACCENT + "0d", boxShadow: `0 0 0 2px ${ACCENT}` }
                          : undefined
                      }
                    >
                      <div className="text-sm font-medium text-zinc-900 dark:text-white">{m.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{m.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {esPool ? (
              <PiezasPorCantidad form={form} set={set} unidadLabel={unidadLabel} />
            ) : (
            <div className="space-y-2">
            <div className="text-xs font-medium text-zinc-500">¿Cómo mides el estado de cada pieza?</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {TIPOS_ESCALA.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() =>
                    set({
                      escalaTipo: t.value,
                      escalaEtiquetas:
                        t.value === "etiquetas" && !(form.escalaEtiquetas || []).length
                          ? [...ESCALA_DEFAULT.etiquetas]
                          : form.escalaEtiquetas,
                    })
                  }
                  className={`text-left rounded-lg border p-2.5 transition ${
                    form.escalaTipo === t.value
                      ? "border-transparent"
                      : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300"
                  }`}
                  style={
                    form.escalaTipo === t.value
                      ? { backgroundColor: ACCENT + "0d", boxShadow: `0 0 0 2px ${ACCENT}` }
                      : undefined
                  }
                >
                  <div className="text-sm font-medium text-zinc-900 dark:text-white">{t.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{t.hint}</div>
                </button>
              ))}
            </div>
            {form.escalaTipo === "etiquetas" && (
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Niveles, del más lleno al más vacío
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {(form.escalaEtiquetas || []).map((et, i) => (
                    <span key={i} className="inline-flex items-center gap-1">
                      <input
                        type="text"
                        value={et}
                        onChange={(e) =>
                          set({
                            escalaEtiquetas: form.escalaEtiquetas.map((x, j) => (j === i ? e.target.value : x)),
                          })
                        }
                        className="w-24 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => set({ escalaEtiquetas: form.escalaEtiquetas.filter((_, j) => j !== i) })}
                        className="text-zinc-400 hover:text-red-500"
                        title="Quitar nivel"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => set({ escalaEtiquetas: [...(form.escalaEtiquetas || []), ""] })}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300"
                  >
                    <PlusIcon className="h-3 w-3" />
                    Nivel
                  </button>
                </div>
              </div>
            )}
            {esAlta && (
              <p className="text-xs text-zinc-500">
                Las piezas se dan de alta (una por una o en lote) desde la ficha del servicio.
              </p>
            )}
            </div>
            )}
          </div>
        </Pregunta>
        )}
      </div>
      )}

      {/* ── Precio al cliente: al final, plano por default ── */}
      {esDescuento ? (
        <div className="border-t border-zinc-200 dark:border-zinc-700/60 pt-4 space-y-2">
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Monto del descuento (opcional)
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={fmtMXNInput(form.precio)}
            onChange={(e) => set({ precio: normalizeMoneyInput(e.target.value) })}
            placeholder="$0"
            className={`${inputClass} max-w-xs`}
          />
          <p className="text-xs text-zinc-500">
            Si lo dejas vacío, el monto se captura al aplicarlo en la cotización.
          </p>
        </div>
      ) : (
      <div className="border-t border-zinc-200 dark:border-zinc-700/60 pt-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">¿Cómo se cobra?</label>
            <select
              value={form.unidad}
              onChange={(e) => set({ unidad: e.target.value })}
              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
            >
              {UNIDADES.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
          {form.unidad === "otra" && (
            <input
              type="text"
              value={form.unidadNombre}
              onChange={(e) => set({ unidadNombre: e.target.value })}
              placeholder="Nombre de la unidad"
              className={`${inputClass} max-w-[12rem]`}
            />
          )}
          {/* Marca los servicios que SOLO existen para ir dentro de una renta
              —limpieza de baños— y por eso no se cotizan solos. El valet
              parking no la lleva: va incluido en unas rentas y se cobra
              aparte en otros eventos. No aplica a una renta, que siempre se
              cobra. */}
          {!esRentaForm && (
            <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 mb-2">
              <input
                type="checkbox"
                checked={!!form.incluidoEnRenta}
                onChange={(e) => set({ incluidoEnRenta: e.target.checked })}
              />
              Está incluido en la renta del venue
            </label>
          )}
        </div>
        {!form.incluidoEnRenta || esRentaForm ? (
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Precio al cliente
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={fmtMXNInput(form.precio)}
              onChange={(e) => set({ precio: normalizeMoneyInput(e.target.value) })}
              placeholder="$0"
              className={`${inputClass} max-w-xs`}
            />
            <p className="mt-1 text-xs text-zinc-500">
              Lo que se cobra por {unidadLabel} al agregarlo a una cotización. En la
              cotización se puede ajustar para un evento en concreto.
            </p>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">
            No se cobra por separado: se le siguen llevando costos, pero no
            aparecerá en las cotizaciones.
          </p>
        )}
      </div>
      )}

      {/* ── Variantes ── */}
      {!esDescuento && (
      <div className="border-t border-zinc-200 dark:border-zinc-700/60 pt-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="text-xs font-medium text-zinc-500">
            Variantes (opcional) — mismo servicio, precio distinto según la condición: martes, sábado, con luces.
          </div>
          <button
            type="button"
            onClick={() =>
              set({
                variantes: [
                  ...(form.variantes || []),
                  { id: null, nombre: "", precio: "", costoProveedor: "" },
                ],
              })
            }
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold hover:underline"
            style={{ color: ACCENT }}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Variante
          </button>
        </div>
        {(form.variantes || []).map((v, idx) => (
          <div key={v.id || idx} className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={v.nombre}
                onChange={(e) => setVariante(idx, { nombre: e.target.value })}
                placeholder="Nombre de la variante (Sábado, Con luces…)"
                className={`${inputClass} max-w-md`}
              />
              <button
                type="button"
                onClick={() => set({ variantes: form.variantes.filter((_, i) => i !== idx) })}
                className="ml-auto text-zinc-400 hover:text-red-500 p-1"
                title="Quitar variante"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <PreviewVariante nombreServicio={form.nombre} nombreVariante={v.nombre} />
            <div className={`grid grid-cols-1 ${esProveedor ? "sm:grid-cols-2" : ""} gap-3`}>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1 whitespace-nowrap">
                  Precio al cliente
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fmtMXNInput(v.precio || "")}
                  onChange={(e) => setVariante(idx, { precio: normalizeMoneyInput(e.target.value) })}
                  placeholder="$0"
                  className={inputClass}
                />
              </div>
              {esProveedor && (
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1 whitespace-nowrap">
                    Costo del proveedor (opcional)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={fmtMXNInput(v.costoProveedor || "")}
                    onChange={(e) =>
                      setVariante(idx, { costoProveedor: normalizeMoneyInput(e.target.value) })
                    }
                    placeholder="$0"
                    className={inputClass}
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-500">
              Lo que dejes vacío lo hereda del servicio.
            </p>
          </div>
        ))}
      </div>
      )}

      {/* ── Inversión (historia: al dar de alta o al clasificar un concepto
          viejo que todavía no la tiene) ── Solo para lo que es del venue: no
          compras lo que trae un proveedor, y preguntarlo al clasificarlo era
          pedir un dato que no existe. ── */}
      {esAlta && !esDescuento && !esProveedor && !esRentaForm && (
        <div className="border-t border-zinc-200 dark:border-zinc-700/60 pt-4">
          {/* Abierto, no plegado: es opcional, pero plegado nadie lo ve y la
              recuperación de la inversión se queda sin el único dato que
              necesita. */}
          <div className="text-xs font-medium text-zinc-500">
            ¿Lo compraste? Registra la inversión (opcional)
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Cuánto costó</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fmtMXNInput(form.adquisicionMonto)}
                  onChange={(e) => set({ adquisicionMonto: normalizeMoneyInput(e.target.value) })}
                  placeholder="0"
                  className={`${inputClass} pl-7`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Fecha de compra</label>
              <input
                type="date"
                value={form.adquisicionFecha}
                onChange={(e) => set({ adquisicionFecha: e.target.value })}
                className={inputClass}
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300 mb-2">
              <input
                type="checkbox"
                checked={!!form.registrarGasto}
                onChange={(e) => set({ registrarGasto: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                Registrarla como egreso en Cuentas
                <span className="block text-xs text-zinc-500">Márcalo si la compra todavía no está capturada en Cuentas.</span>
              </span>
            </label>
          </div>
          <p className="mt-2 text-xs text-zinc-500">
            Con esto el panel te dice cuánto llevas recuperado y en qué evento se pagó solo.
          </p>
        </div>
      )}

      {/* Alta de proveedor sin salirse del flujo. El snapshot de VenueProvider
          lo mete a la lista; onCreated lo deja elegido. */}
      <ProveedorCreateDialog
        open={showNuevoProveedor}
        onClose={() => setShowNuevoProveedor(false)}
        onCreated={(id) => set({ deProveedor: true, proveedorId: id })}
      />
    </div>
  );
}

// Qué trae la renta: servicios del catálogo, con la cantidad que trae de cada
// uno. La cantidad es un DEFAULT —"100 sillas"—, no una amarra: al cotizar la
// renta cada incluido llega pre-seleccionado con esta cantidad y el owner
// desmarca o ajusta lo que ese evento no lleve.
//
// No se captura texto libre a propósito: un incluido es un servicio del
// catálogo, y así llega con lo que ya tiene configurado —si lo trae un
// proveedor, su costo se registra solo al cotizar la renta; si lleva piezas,
// se pueden reservar—. Un renglón de texto no podría hacer ninguna de las dos.
//
// Solo entran los servicios marcados "incluido en la renta": una ambulancia o
// un valet son del venue pero se COBRAN aparte, y meterlos dentro sería
// prometer gratis lo que se vende (ver puedeIrIncluido en utils/catalogo.js).
function IncluyeEditor({ incluye = [], onChange, itemId = null, proveedores = [] }) {
  const { conceptos } = useVenue();
  const [aAgregar, setAAgregar] = useState("");

  const lista = useMemo(() => incluyeDe({ incluye }), [incluye]);
  const yaEsta = useMemo(() => new Set(lista.map((x) => x.conceptoId)), [lista]);

  const porId = useMemo(() => {
    const m = {};
    (conceptos || []).forEach((c) => {
      if (c?.id) m[c.id] = c;
    });
    return m;
  }, [conceptos]);

  // Lo que puede entrar: ni otra renta (un nivel y punto), ni un descuento, ni
  // ella misma, ni algo que ya esté en la lista.
  const candidatos = useMemo(
    () => (conceptos || []).filter((c) => puedeIrIncluido(c, itemId) && !yaEsta.has(c.id)),
    [conceptos, itemId, yaEsta]
  );

  const agregar = (conceptoId) => {
    if (!conceptoId || yaEsta.has(conceptoId)) return;
    onChange?.([...lista, { conceptoId, cantidad: 1 }]);
    setAAgregar("");
  };

  const setCantidad = (conceptoId, valor) =>
    onChange?.(
      lista.map((x) =>
        x.conceptoId === conceptoId ? { ...x, cantidad: Number(valor) > 0 ? Number(valor) : 1 } : x
      )
    );

  const quitar = (conceptoId) =>
    onChange?.(lista.filter((x) => x.conceptoId !== conceptoId));

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 space-y-3">
      <div className="text-xs font-medium text-zinc-500">
        ¿Qué incluye? (opcional) — al cotizar esta renta, todo esto llega marcado y se
        puede desmarcar lo que ese evento no lleve.
      </div>

      {lista.length > 0 && (
        <ul className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
          {lista.map(({ conceptoId, cantidad }) => {
            const c = porId[conceptoId];
            const pid = c ? proveedorDe(c) : null;
            const prov = pid
              ? (proveedores || []).find((x) => x.id === pid)?.nombre || c?.proveedorNombre
              : null;
            return (
              <li key={conceptoId} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-900 dark:text-white truncate">
                    {c?.nombre || "Servicio eliminado del catálogo"}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {!c
                      ? "Ya no existe: quítalo de la lista."
                      : prov
                      ? `Lo da ${prov}`
                      : precioDe(c) > 0
                      ? `Del venue · vale ${"$"}${precioDe(c).toLocaleString("es-MX")} /${unidadDe(c)}`
                      : "Del venue"}
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <span className="whitespace-nowrap">Cantidad</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={cantidad}
                    onChange={(e) => setCantidad(conceptoId, e.target.value)}
                    className="w-20 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-sm"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => quitar(conceptoId)}
                  className="text-zinc-400 hover:text-red-500 p-1"
                  title="Quitar de lo incluido"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ConceptoPicker
        conceptos={candidatos}
        proveedores={proveedores}
        value={aAgregar}
        onChange={agregar}
        placeholder={
          candidatos.length
            ? "Agregar un servicio a lo incluido…"
            : "No hay servicios marcados como incluidos en la renta"
        }
        disabled={candidatos.length === 0}
      />
      {/* Sin esto el picker es un callejón sin salida: el owner busca la
          ambulancia, no la encuentra y no hay nada que le diga por qué. */}
      <p className="text-xs text-zinc-500">
        Solo aparecen los servicios marcados <strong>“Está incluido en la renta”</strong>.
        Lo que se cobra aparte —una ambulancia, el valet— no puede ir dentro: si este
        venue sí lo da incluido, márcalo así en su ficha y aquí aparecerá.
      </p>
      {lista.length === 0 && (
        <p className="text-xs text-zinc-500">
          Una renta sin incluidos funciona igual: entra a la cotización como un
          renglón normal.
        </p>
      )}
    </div>
  );
}

// Modo por cantidad: el total y los renglones de lo que está fuera de
// circulación. Lo reservable se CALCULA, no se captura, para que no pueda
// quedar inconsistente — y lo ocupado no aparece aquí porque nunca se captura:
// sale de los eventos.
function PiezasPorCantidad({ form, set, unidadLabel }) {
  const lista = Array.isArray(form.noDisponibles) ? form.noDisponibles : [];
  const total = Math.max(0, Math.floor(Number(form.piezasTotales) || 0));
  const fuera = lista.reduce((s, x) => s + Math.max(0, Math.floor(Number(x?.cantidad) || 0)), 0);
  const reservables = Math.max(0, total - fuera);

  const setFila = (idx, patch) =>
    set({ noDisponibles: lista.map((x, i) => (i === idx ? { ...x, ...patch } : x)) });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            ¿Cuántas tienes en total?
          </label>
          <input
            type="number"
            min="0"
            step="1"
            value={form.piezasTotales}
            onChange={(e) => set({ piezasTotales: e.target.value })}
            placeholder="240"
            className={`${inputClass} max-w-[10rem]`}
          />
        </div>
        {total > 0 && (
          <div className="text-xs text-zinc-500 pb-2">
            {fuera > 0 ? (
              <>
                {total} totales − {fuera} no disponibles ={" "}
                <span className="font-semibold text-zinc-700 dark:text-zinc-300">
                  {reservables} reservables
                </span>
              </>
            ) : (
              <>
                <span className="font-semibold text-zinc-700 dark:text-zinc-300">
                  {total} reservables
                </span>{" "}
                por {unidadLabel}
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2">
          <div className="text-xs font-medium text-zinc-500">
            ¿Hay piezas fuera de circulación? (opcional)
          </div>
          <button
            type="button"
            onClick={() =>
              set({
                noDisponibles: [
                  ...lista,
                  { id: nuevoIdNoDisponible(), nombre: "", cantidad: "" },
                ],
              })
            }
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold hover:underline"
            style={{ color: ACCENT }}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Renglón
          </button>
        </div>
        {lista.length === 0 ? (
          <p className="mt-1 text-xs text-zinc-500">
            Mantenimiento, prestadas, en otra bodega: lo que pongas aquí resta de
            lo que se puede apartar por evento.
          </p>
        ) : (
          <div className="mt-2 space-y-2">
            {lista.map((x, idx) => (
              <div key={x.id || idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={x.nombre}
                  onChange={(e) => setFila(idx, { nombre: e.target.value })}
                  placeholder="Mantenimiento, Bodega Cuernavaca, Prestadas…"
                  className={`${inputClass} flex-1`}
                />
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={x.cantidad}
                  onChange={(e) => setFila(idx, { cantidad: e.target.value })}
                  placeholder="0"
                  className="w-24 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => set({ noDisponibles: lista.filter((_, i) => i !== idx) })}
                  className="text-zinc-400 hover:text-red-500 p-1"
                  title="Quitar renglón"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-zinc-500">
        Cuántas están ocupadas no se captura: sale de los eventos que ya las
        tienen apartadas.
      </p>
    </div>
  );
}

// Tarjeta de pregunta con casilla: cerrada es una línea; abierta despliega
// lo mínimo que esa dimensión necesita.
// Cómo se va a leer la variante en el catálogo y en la cotización. El nombre
// se CONCATENA con el del servicio (nombreConVariante), así que "Ambulancia"
// + "Ambulancia 24h" sale "Ambulancia · Ambulancia 24h". Enseñar el
// resultado evita el nombre repetido mejor que cualquier instrucción.
export function PreviewVariante({ nombreServicio, nombreVariante }) {
  const servicio = String(nombreServicio || "").trim();
  const variante = String(nombreVariante || "").trim();
  if (!variante) return null;
  const completo = nombreConVariante({ nombre: servicio }, { nombre: variante });
  // "Ambulancia · Ambulancia 24h": la variante repite el nombre del servicio.
  const repite =
    !!servicio &&
    variante.toLowerCase().startsWith(servicio.toLowerCase()) &&
    variante.length > servicio.length;
  const sugerido = repite ? variante.slice(servicio.length).replace(/^[\s·\-–—:]+/, "") : "";
  return (
    <div className="text-xs">
      <span className="text-zinc-500">Se verá como </span>
      <span className="font-medium text-zinc-700 dark:text-zinc-300">{completo}</span>
      {repite && sugerido && (
        <div className="mt-0.5 text-amber-600">
          El nombre del servicio ya va al principio; con “{sugerido}” quedaría “
          {nombreConVariante({ nombre: servicio }, { nombre: sugerido })}”.
        </div>
      )}
    </div>
  );
}

function Pregunta({ activa, titulo, hint, onToggle, children }) {
  return (
    <div
      className={`rounded-lg border p-3 transition ${
        activa ? "border-transparent" : "border-zinc-200 dark:border-zinc-700"
      }`}
      style={activa ? { backgroundColor: ACCENT + "0d", boxShadow: `0 0 0 2px ${ACCENT}` } : undefined}
    >
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!activa}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="block text-sm font-semibold text-zinc-900 dark:text-white">{titulo}</span>
          <span className="block text-xs text-zinc-500">{hint}</span>
        </span>
      </label>
      {activa && <div className="mt-3 pl-6">{children}</div>}
    </div>
  );
}
